/**
 * Notices that a newer build has been deployed while this page kept running.
 *
 * IRES Office ships no service worker, so there is nothing that could install a waiting worker and
 * report itself. Instead the build writes its identity to `/version.json` (see `vue.config.js`) and
 * this checker polls that file, comparing it with the compile-time constants of the running bundle.
 *
 * Everything the checker touches is injectable — the update source, the timer, the visibility
 * subscription and the "can poll now" gate — so it is unit-testable without a browser that can
 * actually go offline or hide a tab.
 *
 * Deploy requirement: `/version.json` must never be long-cached. `firebase.json` serves the `**`
 * catch-all as `no-cache`, and the request below adds `cache: "no-store"` plus a cache-busting
 * query parameter on top. See docs/operations/app-update-notification.md.
 */

import {
  BUILD_MANIFEST_PATH,
  BuildIdentity,
  getBuildIdentityKey,
  getRunningBuild,
  parseBuildManifest,
} from "@/utils/BuildInfo";
import { markIntentionalNavigation } from "@/utils/IntentionalNavigation";

/**
 * How often a tab left open re-checks the deployed build.
 *
 * 30 minutes: often enough that nobody works a full afternoon on a superseded build, rare enough
 * that an idle tab costs a handful of requests a day. Visibility changes are checked on top of
 * this, so a tab left open overnight notices as soon as it is looked at again.
 */
export const APP_UPDATE_POLL_INTERVAL_MS = 30 * 60 * 1000;

/** The seams the checker runs on. Every one has a browser default. */
export interface AppUpdateCheckerSeams {
  /** The update source. Resolves the deployed build, or `null` when it cannot be established. */
  fetchDeployedBuild: () => Promise<BuildIdentity | null>;
  /** Gate: `false` when a poll would be wasted (tab hidden, browser offline). */
  canPollNow: () => boolean;
  /** Timer seam. Returns a handle for {@link AppUpdateCheckerSeams.clearInterval}. */
  setInterval: (handler: () => void, intervalMs: number) => number;
  clearInterval: (handle: number) => void;
  /** Subscribes to "the tab became visible again". Returns an unsubscribe function. */
  onBecameVisible: (handler: () => void) => () => void;
  /** Reload seam, so tests can assert what happens before the page goes away. */
  reload: () => void;
  intervalMs: number;
}

/**
 * Reads `/version.json` bypassing every cache.
 *
 * A non-OK response, a body that is not JSON, or a body that is not a complete identity all
 * resolve to `null`: no information, not "an update is available".
 */
async function fetchDeployedBuildFromManifest(): Promise<BuildIdentity | null> {
  const response = await fetch(`${BUILD_MANIFEST_PATH}?t=${Date.now()}`, {
    cache: "no-store",
    headers: { "Cache-Control": "no-cache" },
  });

  if (!response.ok) {
    return null;
  }

  return parseBuildManifest(await response.json());
}

/**
 * Whether polling is worth doing right now.
 *
 * A hidden tab cannot show the banner anyway, and an offline browser resolves the request to
 * nothing useful while still costing a failed fetch.
 */
function canPollInBrowser(): boolean {
  if (typeof document !== "undefined" && document.hidden) {
    return false;
  }

  return !(typeof navigator !== "undefined" && navigator.onLine === false);
}

function subscribeToVisibility(handler: () => void): () => void {
  const listener = () => {
    if (!document.hidden) {
      handler();
    }
  };

  document.addEventListener("visibilitychange", listener);

  return () => document.removeEventListener("visibilitychange", listener);
}

function createDefaultSeams(): AppUpdateCheckerSeams {
  return {
    fetchDeployedBuild: fetchDeployedBuildFromManifest,
    canPollNow: canPollInBrowser,
    setInterval: (handler, intervalMs) => window.setInterval(handler, intervalMs),
    clearInterval: (handle) => window.clearInterval(handle),
    onBecameVisible: subscribeToVisibility,
    reload: () => window.location.reload(),
    intervalMs: APP_UPDATE_POLL_INTERVAL_MS,
  };
}

export class AppUpdateChecker {
  private readonly seams: AppUpdateCheckerSeams;
  private readonly runningBuildKey: string;
  private intervalHandle: number | null = null;
  private unsubscribeVisibility: (() => void) | null = null;

  /**
   * @param onUpdateDetected Called with the deployed build each time it differs from the running
   *   one. Called again for a build seen after the first, so a second deploy resurfaces the offer.
   * @param seams Overrides for testing. Anything omitted uses the browser default.
   * @param runningBuild The build this bundle was compiled as. Resolved eagerly and on purpose:
   *   a missing compile-time constant must surface where the checker is created, not be swallowed
   *   inside a poll whose failures are all no-ops.
   */
  constructor(
    private readonly onUpdateDetected: (deployed: BuildIdentity) => void,
    seams: Partial<AppUpdateCheckerSeams> = {},
    runningBuild: BuildIdentity = getRunningBuild()
  ) {
    this.seams = { ...createDefaultSeams(), ...seams };
    this.runningBuildKey = getBuildIdentityKey(runningBuild);
  }

  /** Starts polling, checks once immediately, and re-checks whenever the tab becomes visible. */
  start(): void {
    if (this.intervalHandle !== null) {
      return;
    }

    this.intervalHandle = this.seams.setInterval(
      () => void this.checkNow(),
      this.seams.intervalMs
    );

    this.unsubscribeVisibility = this.seams.onBecameVisible(() => void this.checkNow());

    // A tab whose index.html came from a cache can already be behind on load.
    void this.checkNow();
  }

  stop(): void {
    if (this.intervalHandle !== null) {
      this.seams.clearInterval(this.intervalHandle);
      this.intervalHandle = null;
    }

    if (this.unsubscribeVisibility !== null) {
      this.unsubscribeVisibility();
      this.unsubscribeVisibility = null;
    }
  }

  /**
   * One check. Never throws and never reports an update it is not sure about: a skipped poll, a
   * failed request and a malformed manifest are all no-ops.
   */
  async checkNow(): Promise<void> {
    if (!this.seams.canPollNow()) {
      return;
    }

    let deployed: BuildIdentity | null;

    try {
      deployed = await this.seams.fetchDeployedBuild();
    } catch (error) {
      // Offline, DNS, a hosting hiccup, a body that is not JSON. Nothing is known, so nothing is
      // shown; the next poll tries again. Deliberately silent — no console output in this app.
      return;
    }

    if (deployed === null) {
      return;
    }

    if (getBuildIdentityKey(deployed) === this.runningBuildKey) {
      return;
    }

    this.onUpdateDetected(deployed);
  }

  /**
   * Applies the update: marks the reload as intentional, then reloads.
   *
   * The order matters. Marking first is what stops a leave guard from asking the user to confirm
   * the button they just pressed. The reload lands on the new build because `index.html` is served
   * `no-cache` (revalidated on every load) and the bundles it names are content-hashed.
   */
  applyUpdate(): void {
    markIntentionalNavigation();
    this.seams.reload();
  }
}
